import React, { useState } from 'react';
import { Wand2, X, ImagePlus, Trash2 } from 'lucide-react';

/**
 * Fixing one shot that came back wrong.
 *
 * This is the loop that matters in practice. A shot list is approved once; a
 * film is corrected shot by shot, because what is wrong is almost never the
 * writing - it is that the model put water beside a railroad that ran down the
 * middle of a street, or made live crabs look dead.
 *
 * ⚠️ IT SAYS WHAT A REDO COSTS, ON THE BUTTON. Every regeneration is eighty
 * cents. Nothing here should feel free when it is not.
 *
 * ⚠️ A REFERENCE PHOTOGRAPH BEATS AN ADJECTIVE. Describing a real place to a
 * model is guesswork; handing it a photograph of that place is not. So the
 * reference picker sits beside the prompt, not behind a menu.
 *
 * ⚠️ THE OLD CLIP IS NOT DELETED. A redo replaces which take the film uses and
 * leaves the paid clip in the Media Library, so a worse second attempt is not a
 * loss.
 */
export default function ShotEditor({ shot, busy, onClose, onSave, onRedo, onPickReference, references = [] }) {
  const [prompt, setPrompt] = useState(shot.prompt || '');
  const [refs, setRefs] = useState(references);
  // Which model redoes it. Not a setting buried elsewhere: the two are the same
  // price and they are good at different things, so the choice belongs on the
  // shot being fixed.
  const [provider, setProvider] = useState('veo');
  const dirty = prompt.trim() !== String(shot.prompt || '').trim()
    || refs.map((r) => r.id).join(',') !== references.map((r) => r.id).join(',');

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1100,
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, overflowY: 'auto',
    }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ maxWidth: 720, width: '100%' }}>
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Shot {shot.number}</span>
          <button className="btn btn-ghost" onClick={onClose} style={{ padding: 4 }}><X size={15} /></button>
        </div>
        <div className="card-body">
          <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 4 }}>The line it is on screen for</div>
          <div style={{ fontSize: 13, color: 'var(--navy)', fontWeight: 600, lineHeight: 1.5, marginBottom: 14 }}>
            {shot.script_text || <em style={{ color: 'var(--gray-500)' }}>no line</em>}
          </div>

          <label className="form-label">What the camera should see</label>
          <textarea className="form-textarea" rows={6} value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Describe the shot. Say what was wrong last time in plain words." />
          <div style={{ fontSize: 11, color: 'var(--gray-500)', margin: '4px 0 14px' }}>
            Correct it the way you would tell a camera operator. &ldquo;The railroad ran down the middle of
            the street through town and only reached the water at the very end&rdquo; works better than
            adjectives.
          </div>

          <label className="form-label">Reference photographs</label>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 8 }}>
            Up to three. A real photograph of the place settles what words cannot. Stills only, and these
            replace the film&rsquo;s own character references for this shot.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {refs.map((r) => (
              <div key={r.id} style={{ position: 'relative', width: 96 }}>
                <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(r.thumb || r.key)}`}
                  alt={r.name} style={{ width: 96, height: 62, objectFit: 'cover', borderRadius: 4, display: 'block', border: '1px solid var(--gray-200)' }} />
                <button onClick={() => setRefs(refs.filter((x) => x.id !== r.id))}
                  title="Remove" style={{
                    position: 'absolute', top: -6, right: -6, border: 'none', borderRadius: '50%',
                    width: 18, height: 18, background: '#c0392b', color: '#fff', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                  }}><Trash2 size={9} /></button>
              </div>
            ))}
            {refs.length < 3 ? (
              <button className="btn btn-outline" disabled={busy}
                style={{ width: 96, height: 62, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, fontSize: 10 }}
                onClick={() => onPickReference((asset) => setRefs((cur) => [...cur, {
                  id: asset.id, name: asset.suggested_name,
                  thumb: asset.r2_thumb_key, key: asset.r2_original_key,
                }]))}>
                <ImagePlus size={14} />
                Add a photo
              </button>
            ) : null}
          </div>

          <label className="form-label">Which model</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
            {[
              { id: 'veo', label: 'Veo Fast', note: 'quicker, better light' },
              { id: 'sora', label: 'Sora 2', note: 'better on period costume' },
            ].map((o) => (
              <button key={o.id} onClick={() => setProvider(o.id)}
                className={provider === o.id ? 'btn btn-navy' : 'btn btn-outline'}
                style={{ fontSize: 11, padding: '5px 10px' }}>
                {o.label}
                <span style={{ opacity: 0.75, marginLeft: 5, fontWeight: 400 }}>{o.note}</span>
              </button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 16 }}>
            They cost the same. If a shot comes back wrong twice on one, try the other.
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn btn-outline" disabled={busy || !dirty}
              onClick={() => onSave({ prompt, referenceAssetIds: refs.map((r) => r.id) })}>
              Save without redoing it
            </button>
            <button className="btn btn-primary" disabled={busy}
              onClick={() => onRedo({ prompt, referenceAssetIds: refs.map((r) => r.id), provider })}>
              <Wand2 size={13} style={{ verticalAlign: -2, marginRight: 5 }} />
              {busy ? 'Working…' : 'Redo this shot · $0.80'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 8, textAlign: 'right' }}>
            The clip you have now stays in the Media Library either way.
          </div>
        </div>
      </div>
    </div>
  );
}
